/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable sonarjs/no-nested-switch */
/* eslint-disable sonarjs/no-duplicate-string */
import React, { useEffect, useState } from 'react';
import { Tooltip } from '@material-tailwind/react';
import Image from 'next/image';
import { useTranslate } from '@tolgee/react';
import { Typography } from '@/components/Atoms';
import LoadingBalls from '@/components/Common/LoadingBalls';
import InventoryTwoSvg from '@/components/Svg/InventoryTwoSvg';
import { formatNumberDecimals, numberSeparator } from '@/utils/numberSeparator';
import { useStore } from '@/zustand/store/store';
import api from '../../../../api/services/inventory';
import LoginSection from '../../LoginSection';
import styles from './InventorySection.module.scss';
import ItemBoxAsset from './ItemBoxAsset';
import useAuthStatuses from '@/hooks/authentication/useAuthStatuses';

enum EInventoryTabActiveProps {
  elements = 'Elements',
  assets = 'Assets',
  blueprint = 'Blueprints',
}

const gtmPrefix = 'gtm-command_center_inventory_tabs-';

const InventorySection = () => {
  const { t } = useTranslate('commandCenter');
  const {
    commandSocketData: { stateChanged },
  } = useStore();
  const [inventoryTabActive, setInventoryTabActive] = useState<EInventoryTabActiveProps>(EInventoryTabActiveProps.elements);
  const { isLogin } = useAuthStatuses();
  const [isLoading, setIsLoading] = useState(true);
  const [tabSection, setTabSection] = useState<React.ReactNode>(<></>);

  const onClickInventoryTab = (activeItem) => {
    setInventoryTabActive(activeItem);
  };

  const fetchTabData = () => {
    if (!isLogin) {
      setTabSection(<div>Please log in to view your inventory.</div>);
      return;
    }

    setIsLoading(true);

    switch (inventoryTabActive) {
      case EInventoryTabActiveProps.elements:
        api
          .getUserDataInventory()
          .then((res) => {
            const materials = res.data?.user_materials;
            setTabSection(
              <>
                {materials == null || materials.length === 0 ? (
                  <div className='col-span-12 text-center h-full flex justify-center items-center lg:text-[11px] '>
                    <Typography size='s12ToS12'>{t('RIGHT_SECTION.inventorySection.elementsSection.description')}</Typography>
                  </div>
                ) : (
                  <>
                    {materials.map((item, index) => {
                      return (
                        <Tooltip
                          key={index}
                          content={numberSeparator(item.amount) + ' ' + item.unit}
                          placeholder=''
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        >
                          <div className='lg:col-span-4 md:col-span-2 col-span-3 text-center'>
                            <>
                              <div>
                                <Image
                                  src={item.prefix + item.icon}
                                  width={30}
                                  height={30}
                                  layout='responsive'
                                  className='rounded-[4px]'
                                  alt='blueprint'
                                />
                              </div>
                              <div className='flex flex-col items-center justify-center'>
                                <Typography size='s12ToS12'>{item.abbr}</Typography>
                                <Typography size='s12ToS12'>{numberSeparator(formatNumberDecimals(item.amount))}</Typography>
                              </div>
                            </>
                          </div>
                        </Tooltip>
                      );
                    })}
                  </>
                )}
              </>,
            );
          })
          .catch((error) => {
            console.error('Failed to fetch Elements:', error); // Error handling
            setTabSection(<div>Error loading elements.</div>);
          })
          .finally(() => {
            setIsLoading(false);
          });
        break;

      case EInventoryTabActiveProps.assets:
        api
          .inventoryV2()
          .then((res) => {
            const assets = res.data.Data;
            setTabSection(
              <>
                {assets == null || assets.length === 0 ? (
                  <div className='col-span-12 text-center h-full flex justify-center items-center'>
                    <Typography size='s16ToS12'>{t('RIGHT_SECTION.inventorySection.assetsSection.description')}</Typography>
                  </div>
                ) : (
                  <>
                    {assets.map((item, index) => {
                      let assetDetailUrl = '';
                      switch (item.TypeName) {
                        case 'Rover':
                          assetDetailUrl = `https://marketplace.landrocker.io/nft-detail/${item.Id}`;
                          break;
                        case 'Planet':
                          assetDetailUrl = `https://marketplace.landrocker.io/nft-detail/${item.Id}`;
                          break;
                        case 'Companion':
                          assetDetailUrl = `https://marketplace.landrocker.io/nft-detail/${item.Id}`;
                          break;
                        case 'Weapon':
                          assetDetailUrl = `https://marketplace.landrocker.io/nft-detail/${item.Id}`;
                          break;
                        case 'TacticalTools':
                          assetDetailUrl = `https://marketplace.landrocker.io/nft-detail/${item.Id}`;
                          break;
                        case 'suit':
                          assetDetailUrl = `https://marketplace.landrocker.io/nft-detail/${item.Id}`;
                          break;
                        default:
                          assetDetailUrl = null;
                          break;
                      }
                      return <ItemBoxAsset image={item.PrifixAddress + item.Image} name={item.Name} url={assetDetailUrl} key={index} />;
                    })}
                  </>
                )}
              </>,
            );
          })
          .catch((error) => {
            console.error('Failed to fetch Assets:', error); // Error handling
            setTabSection(<div>Error loading assets.</div>);
          })
          .finally(() => {
            setIsLoading(false);
          });
        break;

      case EInventoryTabActiveProps.blueprint:
        api
          .getUserBlueprint()
          .then((res) => {
            const blueprints = res.data.data;
            setTabSection(
              <>
                {blueprints == null || blueprints.length === 0 ? (
                  <div className='col-span-12 text-center h-full flex justify-center items-center'>
                    <Typography size='s16ToS12' className='lg:text-[11px] '>
                      {t('RIGHT_SECTION.inventorySection.blueprintsSection.description')}
                    </Typography>
                  </div>
                ) : (
                  <>
                    {blueprints.map((item, index) => (
                      <ItemBoxAsset image={item.image} name={item.name} url={`/blueprint/${item.row_id}`} key={index} />
                    ))}
                  </>
                )}
              </>,
            );
          })
          .catch((error) => {
            console.error('Failed to fetch Blueprints:', error); // Error handling
            setTabSection(<div>Error loading blueprints.</div>);
          })
          .finally(() => {
            setIsLoading(false);
          });
        break;

      default:
        setTabSection(<div>Select a valid tab to view inventory data.</div>);
        break;
    }
  };

  useEffect(() => {
    fetchTabData();
  }, [inventoryTabActive, isLogin]);

  useEffect(() => {
    if (stateChanged) {
      fetchTabData();
    }
  }, [stateChanged]);

  return (
    <>
      <div className='w-full justify-between flex pb-2'>
        <Typography size='s16ToS12' className='text-[#979797] lg:text-[11px]' isBold>
          {t('RIGHT_SECTION.inventorySection.title')}
        </Typography>
        <InventoryTwoSvg />
      </div>
      <div className='w-full h-[1px] bg-[#4F4F4F]'></div>
      {isLogin ? (
        <>
          <div className='flex mt-2 gap-2 w-full'>
            <Typography
              className={`lg:text-[11px] cursor-pointer transition-all ${gtmPrefix + 'elements'} ${
                inventoryTabActive === EInventoryTabActiveProps.elements ? 'text-[#00d0ab] border-b-[1px] border-b-[#00D0AB]' : 'text-[#979797]'
              } hover:text-[#00d0ab] hover:border-b-[1px] hover:border-b-[#00D0AB]`} // Added hover effect
              size='s16ToS12'
              onClick={() => onClickInventoryTab(EInventoryTabActiveProps.elements)}
            >
              {t('RIGHT_SECTION.inventorySection.elementsSection.title')}
            </Typography>
            <Typography
              className={`lg:text-[11px] cursor-pointer transition-all ${gtmPrefix + 'assets'} ${
                inventoryTabActive === EInventoryTabActiveProps.assets ? 'text-[#00d0ab] border-b-[1px] border-b-[#00D0AB]' : 'text-[#979797]'
              } hover:text-[#00d0ab] hover:border-b-[1px] hover:border-b-[#00D0AB]`} // Added hover effect
              size='s16ToS12'
              onClick={() => onClickInventoryTab(EInventoryTabActiveProps.assets)}
            >
              {t('RIGHT_SECTION.inventorySection.assetsSection.title')}
            </Typography>
            <Typography
              className={`lg:text-[11px] cursor-pointer transition-all ${gtmPrefix + 'blueprints'} ${
                inventoryTabActive === EInventoryTabActiveProps.blueprint ? 'text-[#00d0ab] border-b-[1px] border-b-[#00D0AB]' : 'text-[#979797]'
              } hover:text-[#00d0ab] hover:border-b-[1px] hover:border-b-[#00D0AB]`} // Added hover effect
              size='s16ToS12'
              onClick={() => onClickInventoryTab(EInventoryTabActiveProps.blueprint)}
            >
              {t('RIGHT_SECTION.inventorySection.blueprintsSection.title')}
            </Typography>
          </div>
          <div className='w-full h-[1px] bg-[#9B9B9B] mb-2'></div>
          {isLoading ? <LoadingBalls /> : <div className={styles.boxItems}>{tabSection}</div>}
        </>
      ) : (
        <LoginSection />
      )}
    </>
  );
};

export default InventorySection;
